import { collection, query, where, doc, arrayRemove, arrayUnion, deleteField } from 'firebase/firestore'
import type { DocumentReference, Query } from 'firebase/firestore'
import { updateDoc, deleteDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreDoc, useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { joinGroup, peekPendingGroupJoin, rotateGroupInviteCode } from '../lib/groupJoin'
import { unlinkGroupEvent } from '../lib/groupUnlink'
import { approveGroupEventRequest, rejectGroupEventRequest } from './useGroupPendingRequests'
import type { Group, GroupEvent, GroupEventRequest } from '../types'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { log } from '../lib/logger'

// The fields firestore.rules' isValidGroupUpdate accepts; anything else fails every admin write.
const GROUP_UPDATE_FIELDS = ['name', 'inviteCode', 'createdBy', 'memberIds', 'createdAt', 'imageUrl', 'guestIds', 'adminIds', 'requireLinkApproval']

// Which rule clause a denied admin write broke (GlitchTip 8713658): the client
// gates these actions the same way the rules do, so the doc state is the suspect.
function logAdminWriteDenied(action: string, group: Group | null, viewer: string | null, target: string) {
  if (!group) return log('groups', `${action} failed`, { groupLoaded: false })
  log('groups', `${action} failed`, {
    viewerIsCreator: group.createdBy === viewer,
    viewerInAdminIds: !!viewer && !!group.adminIds?.includes(viewer),
    viewerIsMember: !!viewer && group.memberIds.includes(viewer),
    targetIsSelf: target === viewer,
    targetIsMember: group.memberIds.includes(target),
    targetInAdminIds: !!group.adminIds?.includes(target),
    staleAdminIds: (group.adminIds ?? []).filter(id => !group.memberIds.includes(id)).length,
    unexpectedFields: Object.keys(group).filter(k => k !== 'id' && !GROUP_UPDATE_FIELDS.includes(k)),
    memberCount: group.memberIds.length,
    nameLength: group.name?.length ?? 0,
  })
}

export function useGroupDetail(groupId: string, userId: string | null) {
  const groupQ = useFirestoreDoc<Group>(doc(db, 'groups', groupId) as DocumentReference<Group>)
  const eventsQ = useFirestoreCollection<GroupEvent>(
    query(collection(db, 'groupEvents'), where('groupId', '==', groupId)) as Query<GroupEvent>,
  )

  const groupLoading = groupQ.loading
  const eventsLoading = eventsQ.loading
  const loading = groupLoading || eventsLoading
  const error = groupQ.error ?? eventsQ.error ?? null

  const renameGroup = (name: string) =>
    runMutation(fbt("Couldn't rename group", 'Error toast when renaming a group fails'), async () => {
      await updateDoc(doc(db, 'groups', groupId), { name })
      track('group_renamed', { group_id: groupId })
    })

  // Server-side: the new code must be unique, and other members' group share links get revoked too.
  const rotateInviteCode = () =>
    runMutation(fbt("Couldn't reset invite link", 'Error toast when resetting a group invite link fails'), async () => {
      await rotateGroupInviteCode(groupId)
      track('group_invite_code_rotated', { group_id: groupId })
      toast.success(fbt('Invite link reset', 'Toast after a group admin resets the group invite link'))
    })

  // onGroupDeleted cascades the rest (events, totems, invites, Storage).
  const deleteGroup = () =>
    runMutation(fbt("Couldn't delete group", 'Error toast when deleting a group fails'), async () => {
      await deleteDoc(doc(db, 'groups', groupId))
      track('group_deleted', { group_id: groupId })
    })

  const leaveGroup = async () => {
    if (!userId || groupQ.data?.createdBy === userId) return
    await runMutation(fbt("Couldn't leave group", 'Error toast when leaving a group fails'), async () => {
      const wasAdmin = !!groupQ.data?.adminIds?.includes(userId)
      await updateDoc(doc(db, 'groups', groupId), {
        memberIds: arrayRemove(userId),
        ...(wasAdmin ? { adminIds: arrayRemove(userId) } : {}),
      })
      track('group_left', { group_id: groupId })
    })
  }

  const transferAndLeave = async (newOwnerId: string) => {
    if (!userId || groupQ.data?.createdBy !== userId) return
    await runMutation(fbt("Couldn't transfer the group", 'Error toast when handing a group to a new owner and leaving fails'), async () => {
      await updateDoc(doc(db, 'groups', groupId), { createdBy: newOwnerId, memberIds: arrayRemove(userId) })
      track('group_ownership_transferred', { group_id: groupId })
    })
  }

  const setCover = (imageUrl: string | null) =>
    runMutation(fbt("Couldn't update cover", 'Error toast when changing a group cover image fails'), async () => {
      await updateDoc(doc(db, 'groups', groupId), {
        imageUrl: imageUrl ?? deleteField(),
      })
      if (imageUrl) track('group_cover_set', { group_id: groupId, entry_point: imageUrl.startsWith('/') ? 'preset' : 'upload' })
      else track('group_cover_cleared', { group_id: groupId })
    })

  const unlinkEvent = async (groupEventId: string) => {
    const ge = eventsQ.data.find(g => g.id === groupEventId)
    if (ge) await unlinkGroupEvent(ge, 'group_page')
  }

  const setAdmin = (uid: string, makeAdmin: boolean) =>
    runMutation(fbt("Couldn't update admins", 'Error toast when promoting or demoting a group admin fails'), async () => {
      try {
        await updateDoc(doc(db, 'groups', groupId), { adminIds: makeAdmin ? arrayUnion(uid) : arrayRemove(uid) })
      } catch (err) {
        logAdminWriteDenied(makeAdmin ? 'setAdmin(promote)' : 'setAdmin(demote)', groupQ.data, userId, uid)
        throw err
      }
      track(makeAdmin ? 'group_admin_added' : 'group_admin_removed', { group_id: groupId })
    })

  const removeMember = (uid: string) =>
    runMutation(fbt("Couldn't remove member", 'Error toast when a group admin removing a member fails'), async () => {
      const wasAdmin = !!groupQ.data?.adminIds?.includes(uid)
      try {
        await updateDoc(doc(db, 'groups', groupId), {
          memberIds: arrayRemove(uid),
          ...(wasAdmin ? { adminIds: arrayRemove(uid) } : {}),
        })
      } catch (err) {
        logAdminWriteDenied('removeMember', groupQ.data, userId, uid)
        throw err
      }
      track('group_member_removed', { group_id: groupId })
    })

  const setRequireLinkApproval = (enabled: boolean) =>
    runMutation(fbt("Couldn't update group settings", 'Error toast when changing who can link events to a group fails'), async () => {
      await updateDoc(doc(db, 'groups', groupId), { requireLinkApproval: enabled })
      track(enabled ? 'group_link_approval_enabled' : 'group_link_approval_disabled', { group_id: groupId })
    })

  const approveRequest = async (req: GroupEventRequest) => {
    if (userId) await approveGroupEventRequest(req, userId, eventsQ.data.some(ge => ge.eventId === req.eventId))
  }

  const rejectRequest = async (req: GroupEventRequest) => {
    if (userId) await rejectGroupEventRequest(req, userId)
  }

  const upgradeGuestToMember = async () => {
    if (!userId) return
    await runMutation(fbt("Couldn't join group", 'Error toast when joining a group fails'), async () => {
      // A stashed invite proof covers a guest whose UID changed at sign-in;
      // a guest whose UID survived is covered by their guest slot alone.
      const pending = peekPendingGroupJoin()
      await joinGroup(pending?.groupId === groupId ? pending : { groupId })
      track('group_joined', { group_id: groupId, entry_point: 'guest_upgrade' })
      track('social_connected', { entry_point: 'group' })
    })
  }

  return {
    group: groupQ.data,
    groupEvents: eventsQ.data,
    loading,
    groupLoading,
    eventsLoading,
    error,
    renameGroup,
    rotateInviteCode,
    deleteGroup,
    leaveGroup,
    transferAndLeave,
    unlinkEvent,
    setAdmin,
    removeMember,
    setRequireLinkApproval,
    approveRequest,
    rejectRequest,
    setCover,
    upgradeGuestToMember,
  }
}
